import { useState } from 'react';

/**
 * useDialogState — samlar alla dialog open/close-flaggor + edit-targets
 * från Index.tsx på ett ställe. Returnerar både rå-state och bekväma
 * open/close-callbacks så call-sites blir kortare:
 *
 *   const dlg = useDialogState();
 *   <TopBar onHelp={dlg.openHelp} ... />
 *   <IndexDialogs helpDialogOpen={dlg.helpOpen} setHelpDialogOpen={dlg.setHelpOpen} ... />
 *
 * Varför inte useReducer? Dialogerna är oberoende — ingen kombinerad
 * state-machine. useState per flagga ger lägre cognitive load och
 * tillåter granular re-render (öppning av Help triggar inte Save-rerender).
 */
export interface EditBlockState {
  blockId: string;
  field: 'name' | 'description' | 'color';
}

export interface EditGroupState {
  groupId: string;
  field: 'name' | 'color';
}

export function useDialogState(initialWizardOpen: boolean) {
  // Save / load / import
  const [saveAsNew, setSaveAsNew] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [repoUploadOpen, setRepoUploadOpen] = useState(false);

  // Wizard / new-flow
  const [wizardOpen, setWizardOpen] = useState(initialWizardOpen);
  const [newWorkflowOpen, setNewWorkflowOpen] = useState(false);

  // Info / help
  const [helpOpen, setHelpOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);

  // AI
  const [aiJsonOpen, setAiJsonOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);

  // Export
  const [exportPngOpen, setExportPngOpen] = useState(false);
  const [exportJsonOpen, setExportJsonOpen] = useState(false);

  // Feedback
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  // Auth / sharing
  const [loginOpen, setLoginOpen] = useState(false);
  const [cloudWorkspaceOpen, setCloudWorkspaceOpen] = useState(false);
  const [shareWorkspaceOpen, setShareWorkspaceOpen] = useState(false);
  const [processMotorOpen, setProcessMotorOpen] = useState(false);

  // Confirm-dialogs
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmDeleteBlockOpen, setConfirmDeleteBlockOpen] = useState(false);
  const [confirmDeleteGroupOpen, setConfirmDeleteGroupOpen] = useState(false);

  // Edit dialogs (right-click context-menu targets)
  const [editBlockState, setEditBlockState] = useState<EditBlockState | null>(null);
  const [editGroupState, setEditGroupState] = useState<EditGroupState | null>(null);

  return {
    // Save / load / import
    saveAsNew, setSaveAsNew,
    saveOpen, setSaveOpen,
    loadOpen, setLoadOpen,
    importOpen, setImportOpen,
    openImport: () => setImportOpen(true),
    repoUploadOpen, setRepoUploadOpen,
    openRepoUpload: () => setRepoUploadOpen(true),

    // Wizard
    wizardOpen, setWizardOpen,
    newWorkflowOpen, setNewWorkflowOpen,
    openNewWorkflow: () => setNewWorkflowOpen(true),
    reopenWizard: () => setWizardOpen(true),

    // Info
    helpOpen, setHelpOpen,
    openHelp: () => setHelpOpen(true),
    shortcutsOpen, setShortcutsOpen,
    openShortcuts: () => setShortcutsOpen(true),
    overviewOpen, setOverviewOpen,
    openOverview: () => setOverviewOpen(true),

    // AI
    aiJsonOpen, setAiJsonOpen,
    openAiJson: () => setAiJsonOpen(true),
    aiAssistantOpen, setAiAssistantOpen,
    openAiAssistant: () => setAiAssistantOpen(true),

    // Export
    exportPngOpen, setExportPngOpen,
    exportJsonOpen, setExportJsonOpen,
    openExportJson: () => setExportJsonOpen(true),

    // Feedback
    featureRequestOpen, setFeatureRequestOpen,
    openFeatureRequest: () => setFeatureRequestOpen(true),
    bugReportOpen, setBugReportOpen,
    openBugReport: () => setBugReportOpen(true),

    // Auth / sharing
    loginOpen, setLoginOpen,
    openLogin: () => setLoginOpen(true),
    cloudWorkspaceOpen, setCloudWorkspaceOpen,
    openCloudWorkspace: () => setCloudWorkspaceOpen(true),
    shareWorkspaceOpen, setShareWorkspaceOpen,
    openShareWorkspace: () => setShareWorkspaceOpen(true),
    processMotorOpen, setProcessMotorOpen,
    openProcessMotor: () => setProcessMotorOpen(true),

    // Confirm
    confirmClearOpen, setConfirmClearOpen,
    confirmDeleteBlockOpen, setConfirmDeleteBlockOpen,
    requestDeleteBlock: () => setConfirmDeleteBlockOpen(true),
    confirmDeleteGroupOpen, setConfirmDeleteGroupOpen,
    requestDeleteGroup: () => setConfirmDeleteGroupOpen(true),

    // Edit-targets
    editBlockState, setEditBlockState,
    editGroupState, setEditGroupState,
  };
}
